import { EditButton, DeleteButton } from "@refinedev/antd";
import { Card, Table, Space, Input, Select, Button, Tooltip, message, Badge } from "antd";
import { CommentOutlined } from "@ant-design/icons";
import type { CSSProperties } from "react";
import {
  CalendarOutlined,
  SearchOutlined,
  ClearOutlined,
  CloudDownloadOutlined,
  FileImageOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { PlanningPlotMapsExportModal } from "./components/PlanningPlotMapsExportModal";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { PlanningGroup } from "../../types";
import dayjs from "dayjs";
import { useState, useMemo, useEffect } from "react";
import "./planning-groups.css";
import { usePageTitle } from "../../hooks";
import { PageHeader } from "../../components/common/PageHeader";
import { API_BASE_URL } from "../../utils/constants";
import { useGetIdentity } from "@refinedev/core";
import { canWriteData, type UserInfo } from "../../types/auth";

/**
 * PlanningGroupList Component
 * Fetches ALL planning groups and handles pagination/sorting/filtering locally.
 */
export const PlanningGroupList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: identity } = useGetIdentity<UserInfo>();
  const canWrite = canWriteData(identity);
  const [searchText, setSearchText] = useState("");
  // Filtro por campaña — reemplaza la columna "Campaña" que quitamos
  // porque el nombre de la planning suele llevar el año implícito.
  const [seasonFilter, setSeasonFilter] = useState<string>("");
  const [allRecords, setAllRecords] = useState<PlanningGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  usePageTitle("Planificaciones");

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("access_token") || "";
        const res = await fetch(
          `${API_BASE_URL}/planning-groups/?page_size=9999&ordering=-created`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const data = await res.json();
        setAllRecords(Array.isArray(data?.data) ? data.data : Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : []);
      } catch {
        setAllRecords([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAll();
  }, []);

  const seasonOptions = useMemo(() => {
    const set = new Set<string>();
    allRecords.forEach((r) => {
      const s = (r.season_name || "").trim();
      if (s) set.add(s);
    });
    return Array.from(set)
      .sort((a, b) => b.localeCompare(a))
      .map((s) => ({ label: s, value: s }));
  }, [allRecords]);

  // Local search + season filter
  const filteredRecords = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    const s = seasonFilter.trim();
    return allRecords.filter((r) => {
      if (s && (r.season_name || "") !== s) return false;
      if (!q) return true;
      return (
        (r.name || "").toLowerCase().includes(q) ||
        (r.season_name || "").toLowerCase().includes(q)
      );
    });
  }, [allRecords, searchText, seasonFilter]);

  const handleClearFilters = () => {
    setSearchText("");
    setSeasonFilter("");
  };

  // % completado + color del fill de la fila. Misma paleta que la
  // antigua columna Progress (amber < 50 %, cyan 50-99 %, verde
  // 100 %) pero con alfa baja para no tapar el texto.
  const getCompletionInfo = (record: PlanningGroup) => {
    const total = record.total_audits ?? 0;
    const pending = record.pending_audits ?? 0;
    if (total === 0) return { pct: 0, color: "transparent" as string };
    const pct = Math.round(((total - pending) / total) * 100);
    const color =
      pct >= 100
        ? "rgba(82, 196, 26, 0.20)"
        : pct >= 50
          ? "rgba(19, 194, 194, 0.18)"
          : "rgba(250, 173, 20, 0.20)";
    return { pct, color };
  };

  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  // Modal de exportación en bulk de mapas de parcelas — itera todas
  // las auditorías de la planificación en cliente, generando un PNG
  // por auditoría y empaquetándolas en un ZIP. Ver el componente
  // para detalles del flujo.
  const [mapsExportTarget, setMapsExportTarget] = useState<
    PlanningGroup | null
  >(null);

  const handleDownloadBundle = async (record: PlanningGroup) => {
    setDownloadingId(record.id);
    const hide = message.loading(
      t(
        "planning.downloading_bundle",
        "Generando paquete (KML + Excel + aforo)...",
      ),
      0,
    );
    try {
      const token = localStorage.getItem("access_token") || "";
      const res = await fetch(
        `${API_BASE_URL}/planning-groups/${record.id}/download-bundle/`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `${record.name || "planning"}_bundle.zip`;
      const filename = match?.[1] || fallbackName;
      // El backend expone `X-Bundle-Errors` con el nº de auditorías
      // que quedaron fuera de alguna sección — el detalle está en
      // `_errors/audits_fallidas.txt` dentro del zip. Los avisamos
      // para que el usuario sepa que hay que abrirlo y revisar.
      const errorCount = parseInt(
        res.headers.get("X-Bundle-Errors") || "0",
        10,
      );

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      if (errorCount > 0) {
        message.warning(
          t(
            "planning.download_partial",
            `Paquete descargado. ${errorCount} auditoría(s) han quedado ` +
              `fuera por problemas de datos — revisa ` +
              `_errors/audits_fallidas.txt dentro del zip.`,
            { count: errorCount },
          ),
          8,
        );
      } else {
        message.success(
          t("planning.download_success", "Paquete descargado correctamente"),
        );
      }
    } catch (err) {
      message.error(
        t(
          "planning.download_error",
          "No se pudo generar el paquete. Inténtalo de nuevo.",
        ),
      );
      console.error("download-bundle failed", err);
    } finally {
      hide();
      setDownloadingId(null);
    }
  };

  return (
    <>
    <Card className="planning-groups-list">
      <PageHeader
        icon={<CalendarOutlined />}
        accent="#FA8C16"
        title={t("planning.title", "Planificaciones")}
        subtitle={t(
          "planning.subtitle",
          "Listado de planificaciones y su grado de avance",
        )}
        extra={
          canWrite ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/planning-groups/create")}
            >
              {t("planning.create", "Nueva planificación")}
            </Button>
          ) : null
        }
      />

      <Space size="middle" style={{ marginBottom: 16 }} wrap>
        <Input.Search
          placeholder={t("planning.search", "Buscar por nombre o campaña...")}
          allowClear
          enterButton={<SearchOutlined />}
          size="middle"
          style={{ width: 320 }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={(v) => setSearchText(v)}
        />
        <Select
          allowClear
          placeholder={t("planning.season", "Campaña")}
          value={seasonFilter || undefined}
          onChange={(v) => setSeasonFilter(v ?? "")}
          options={seasonOptions}
          style={{ width: 200 }}
          showSearch
          optionFilterProp="label"
          size="middle"
        />
        <Button icon={<ClearOutlined />} onClick={handleClearFilters} size="middle">
          {t("common.clearFilters", "Limpiar filtros")}
        </Button>
      </Space>

      <Table
          dataSource={filteredRecords}
          loading={isLoading}
          rowKey="id"
          pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (total) => `${total} registros` }}
          scroll={{ x: 1500 }}
          onRow={(record: PlanningGroup) => {
            const { pct, color } = getCompletionInfo(record);
            return {
              onClick: () => navigate(`/planning-groups/show/${record.id}`),
              className: "planning-progress-row",
              style: {
                cursor: "pointer",
                ["--progress-pct" as string]: `${pct}%`,
                ["--progress-color" as string]: color,
              } as CSSProperties,
            };
          }}
        >
          {/* Columns mirror the coordinator/auditor planning list so
              all roles read the same row at a glance. The Actions
              column is admin-only and stays pinned to the right. */}
          <Table.Column
            dataIndex="name"
            title={t("planning.name", "Nombre")}
            sorter={(a: PlanningGroup, b: PlanningGroup) =>
              (a.name || "").localeCompare(b.name || "")
            }
            width={220}
            render={(name: string, record: PlanningGroup) => {
              const unread = record.unread_comments_count ?? 0;
              if (unread <= 0) return name;
              // Badge dot + count al lado del nombre para que el
              // auditor vea de un vistazo qué plannings tienen
              // conversación pendiente sin abrir cada una. El
              // tooltip aclara qué es y a dónde llevaría abrirlo.
              return (
                <Space size={8}>
                  <span>{name}</span>
                  <Tooltip
                    title={t(
                      "planning.unread_comments_tooltip",
                      "{{n}} comentario(s) sin leer en esta planning",
                      { n: unread },
                    )}
                  >
                    <Badge
                      count={unread}
                      overflowCount={99}
                      style={{ backgroundColor: "#faad14" }}
                      offset={[6, -2]}
                    >
                      <CommentOutlined
                        style={{ color: "#faad14", fontSize: 16 }}
                      />
                    </Badge>
                  </Tooltip>
                </Space>
              );
            }}
          />
          <Table.Column
            dataIndex="first_audit_date"
            title={t("planning.start_date", "Fecha Inicio")}
            width={130}
            sorter={(a: PlanningGroup, b: PlanningGroup) => {
              const av = a.first_audit_date ? new Date(a.first_audit_date).getTime() : 0;
              const bv = b.first_audit_date ? new Date(b.first_audit_date).getTime() : 0;
              return av - bv;
            }}
            render={(value: string | null | undefined) =>
              value ? dayjs(value).format("DD/MM/YYYY") : "—"
            }
          />
          <Table.Column
            dataIndex="last_audit_date"
            title={t("planning.finish_date", "Fecha Fin")}
            width={130}
            sorter={(a: PlanningGroup, b: PlanningGroup) => {
              const ap = getCompletionInfo(a).pct;
              const bp = getCompletionInfo(b).pct;
              const av = ap > 95 && a.last_audit_date ? new Date(a.last_audit_date).getTime() : 0;
              const bv = bp > 95 && b.last_audit_date ? new Date(b.last_audit_date).getTime() : 0;
              return av - bv;
            }}
            render={(value: string | null | undefined, record: PlanningGroup) => {
              // Fecha fin sólo cuando la planning está efectivamente
              // cerrada (>95 % de auditorías completadas). Antes se
              // pintaba en cuanto había alguna last_audit_date y
              // confundía — auditor asumía "ya está".
              const { pct } = getCompletionInfo(record);
              if (pct <= 95 || !value) return "—";
              return dayjs(value).format("DD/MM/YYYY");
            }}
          />
          <Table.Column
            title={t("planning.producers", "Productores")}
            key="producers_breakdown"
            width={130}
            align="center"
            sorter={(a: PlanningGroup, b: PlanningGroup) =>
              (a.audited_producers ?? 0) - (b.audited_producers ?? 0)
            }
            render={(_: unknown, record: PlanningGroup) => {
              const audited = record.audited_producers ?? 0;
              const total = record.total_producers ?? 0;
              return (
                <Tooltip
                  title={t(
                    "planning.producers_tooltip",
                    "{{audited}} productores auditados de {{total}}",
                    { audited, total },
                  )}
                >
                  <span>
                    <strong>{audited}</strong>
                    <span style={{ color: "#8c8c8c" }}> / {total}</span>
                  </span>
                </Tooltip>
              );
            }}
          />
          <Table.Column
            title={t("planning.farms", "Fincas")}
            key="farms_breakdown"
            width={130}
            align="center"
            sorter={(a: PlanningGroup, b: PlanningGroup) =>
              (a.audited_farms ?? 0) - (b.audited_farms ?? 0)
            }
            render={(_: unknown, record: PlanningGroup) => {
              const audited = record.audited_farms ?? 0;
              const total = record.total_farms ?? 0;
              return (
                <Tooltip
                  title={t(
                    "planning.farms_tooltip",
                    "{{audited}} fincas auditadas de {{total}}",
                    { audited, total },
                  )}
                >
                  <span>
                    <strong>{audited}</strong>
                    <span style={{ color: "#8c8c8c" }}> / {total}</span>
                  </span>
                </Tooltip>
              );
            }}
          />
          <Table.Column
            title={t("planning.total_audits", "Total auditorías")}
            dataIndex="total_audits"
            key="total_audits"
            width={130}
            align="center"
            sorter={(a: PlanningGroup, b: PlanningGroup) =>
              (a.total_audits ?? 0) - (b.total_audits ?? 0)
            }
            render={(_: unknown, record: PlanningGroup) => {
              const total = record.total_audits ?? 0;
              const pending = record.pending_audits ?? 0;
              const realized = Math.max(total - pending, 0);
              return (
                <Tooltip
                  title={t(
                    "planning.audits_breakdown",
                    "{{realized}} realizadas / {{pending}} pendientes",
                    { realized, pending },
                  )}
                >
                  <span>
                    <strong>{realized}</strong>
                    <span style={{ color: "#8c8c8c" }}> / {total}</span>
                  </span>
                </Tooltip>
              );
            }}
          />
          <Table.Column
            title={t("planning.surface", "Sup. auditada / planificada (ha)")}
            key="surface_breakdown"
            width={210}
            align="right"
            sorter={(a: PlanningGroup, b: PlanningGroup) =>
              (a.audited_surface ?? 0) - (b.audited_surface ?? 0)
            }
            render={(_: unknown, record: PlanningGroup) => {
              const audited = record.audited_surface ?? 0;
              const planned = record.planned_surface ?? 0;
              const fmt = (n: number) =>
                new Intl.NumberFormat("es-ES", { maximumFractionDigits: 1 }).format(n);
              return (
                <Tooltip
                  title={t(
                    "planning.surface_tooltip",
                    "{{audited}} ha auditadas de {{planned}} ha planificadas",
                    { audited: fmt(audited), planned: fmt(planned) },
                  )}
                >
                  <span>
                    <strong>{fmt(audited)}</strong>
                    <span style={{ color: "#8c8c8c" }}> / {fmt(planned)}</span>
                  </span>
                </Tooltip>
              );
            }}
          />
          <Table.Column
            title={t("planning.total_kg_audited", "Kilos auditados")}
            dataIndex="total_kg_audited"
            key="total_kg_audited"
            width={140}
            align="right"
            sorter={(a: PlanningGroup, b: PlanningGroup) =>
              (a.total_kg_audited ?? 0) - (b.total_kg_audited ?? 0)
            }
            render={(v: number | undefined) =>
              new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(
                v ?? 0,
              ) + " kg"
            }
          />
          <Table.Column
            title={t("common.actions", "Acciones")}
            dataIndex="actions"
            width={180}
            fixed="right"
            render={(_: unknown, record: PlanningGroup) => (
              // stopPropagation en el wrapper para que los clicks
              // sobre botones de acción no disparen el onRow que
              // navega al detalle. El click en la fila se reserva
              // para "abrir detalle" — antes había ShowButton
              // explícito, ahora se elimina.
              <Space onClick={(e) => e.stopPropagation()}>
                {canWrite && (
                  <EditButton hideText size="small" recordItemId={record.id} />
                )}
                <Tooltip
                  title={t(
                    "planning.download_bundle",
                    "Descargar paquete (KML + Excel + aforo)",
                  )}
                >
                  <Button
                    size="small"
                    icon={<CloudDownloadOutlined />}
                    loading={downloadingId === record.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadBundle(record);
                    }}
                    aria-label={t(
                      "planning.download_bundle",
                      "Descargar paquete (KML + Excel + aforo)",
                    )}
                  />
                </Tooltip>
                <Tooltip
                  title={t(
                    "planning.download_plot_maps",
                    "Descargar mapas de parcelas de todas las auditorías",
                  )}
                >
                  <Button
                    size="small"
                    icon={<FileImageOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMapsExportTarget(record);
                    }}
                    aria-label={t(
                      "planning.download_plot_maps",
                      "Descargar mapas de parcelas",
                    )}
                  />
                </Tooltip>
                {canWrite && (
                  <DeleteButton hideText size="small" recordItemId={record.id} />
                )}
              </Space>
            )}
          />
        </Table>
    </Card>
    {mapsExportTarget && (
      <PlanningPlotMapsExportModal
        open={!!mapsExportTarget}
        planningId={mapsExportTarget.id}
        planningName={mapsExportTarget.name || `planning-${mapsExportTarget.id}`}
        onClose={() => setMapsExportTarget(null)}
      />
    )}
    </>
  );
};
